import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { AssetKind, DownloadsRankingPeriod } from '@/lib/assets/types'

interface ToggleFiltersOptions {
  shouldPersist?: boolean
}

export type AudioAssetsLayoutPreference = 'row' | 'grid'
export type VideoPreviewPreference = 'auto' | 'on' | 'off'

export type AssetRankingDebugState = {
  enabled: boolean
  period: DownloadsRankingPeriod
  levelOneOnly: boolean
}

interface AppState {
  // Favorites
  favorites: string[]
  addFavorite: (id: string) => void
  removeFavorite: (id: string) => void
  isFavorite: (id: string) => boolean

  // Filters
  filtersVisible: boolean
  previousFiltersVisible: boolean
  showFilters: (options?: ToggleFiltersOptions) => void
  hideFilters: (options?: ToggleFiltersOptions) => void

  // Asset explorer preferences
  videoPreviewPreference: VideoPreviewPreference
  setVideoPreviewPreference: (preference: VideoPreviewPreference) => void
  vfxGridPreviewPreferredVariant: 'b' | 'c'
  setVfxGridPreviewPreferredVariant: (variant: 'b' | 'c') => void
  enableVfxPreviewSoundOnHover: boolean
  setEnableVfxPreviewSoundOnHover: (enabled: boolean) => void
  audioAssetsLayoutPreference: AudioAssetsLayoutPreference
  setAudioAssetsLayoutPreference: (preference: AudioAssetsLayoutPreference) => void
  assetRankingDebug: AssetRankingDebugState
  setAssetRankingDebugEnabled: (enabled: boolean) => void
  setAssetRankingDebugPeriod: (period: DownloadsRankingPeriod) => void
  setAssetRankingDebugLevelOneOnly: (enabled: boolean) => void
  assetRankingDebugAccessGranted: boolean
  setAssetRankingDebugAccessGranted: (granted: boolean) => void

  // Download preferences per vertical
  downloadFormatPreferences: Partial<Record<AssetKind, { format: string; resolution: string | null }>>
  setDownloadFormatPreference: (vertical: AssetKind, format: string, resolution: string | null) => void

  // Internal state
  _hasHydrated: boolean
  setHasHydrated: (state: boolean) => void
}

type PersistedAppState = Pick<
  AppState,
  | 'filtersVisible'
  | 'videoPreviewPreference'
  | 'vfxGridPreviewPreferredVariant'
  | 'enableVfxPreviewSoundOnHover'
  | 'audioAssetsLayoutPreference'
  | 'assetRankingDebug'
  | 'downloadFormatPreferences'
>

const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      favorites: [],
      addFavorite: (id) => set((state) => ({ favorites: [...state.favorites, id] })),
      removeFavorite: (id) => set((state) => ({ favorites: state.favorites.filter((favorite) => favorite !== id) })),
      isFavorite: (id) => get().favorites.includes(id),
      filtersVisible: true,
      previousFiltersVisible: false,
      showFilters: ({ shouldPersist } = { shouldPersist: true }) => {
        set((state) => {
          return { filtersVisible: shouldPersist ? true : state.filtersVisible, previousFiltersVisible: true }
        })
      },
      hideFilters: ({ shouldPersist } = { shouldPersist: true }) => {
        set((state) => {
          return { filtersVisible: shouldPersist ? false : state.filtersVisible, previousFiltersVisible: false }
        })
      },
      videoPreviewPreference: 'auto',
      setVideoPreviewPreference: (preference) => {
        set({
          videoPreviewPreference: preference,
        })
      },
      vfxGridPreviewPreferredVariant: 'b',
      setVfxGridPreviewPreferredVariant: (variant) => {
        set({
          vfxGridPreviewPreferredVariant: variant,
        })
      },
      enableVfxPreviewSoundOnHover: false,
      setEnableVfxPreviewSoundOnHover: (enabled) => {
        set({
          enableVfxPreviewSoundOnHover: enabled,
        })
      },
      audioAssetsLayoutPreference: 'row',
      setAudioAssetsLayoutPreference: (preference) => {
        set({
          audioAssetsLayoutPreference: preference,
        })
      },
      assetRankingDebug: {
        enabled: false,
        period: 'year',
        levelOneOnly: false,
      },
      setAssetRankingDebugEnabled: (enabled) => {
        set((state) => ({
          assetRankingDebug: {
            ...state.assetRankingDebug,
            enabled,
          },
        }))
      },
      setAssetRankingDebugPeriod: (period) => {
        set((state) => ({
          assetRankingDebug: {
            ...state.assetRankingDebug,
            period,
          },
        }))
      },
      setAssetRankingDebugLevelOneOnly: (enabled) => {
        set((state) => ({
          assetRankingDebug: {
            ...state.assetRankingDebug,
            levelOneOnly: enabled,
          },
        }))
      },
      assetRankingDebugAccessGranted: false,
      setAssetRankingDebugAccessGranted: (granted) => {
        set({ assetRankingDebugAccessGranted: granted })
      },
      downloadFormatPreferences: {},
      setDownloadFormatPreference: (vertical, format, resolution) => {
        set((state) => ({
          downloadFormatPreferences: {
            ...state.downloadFormatPreferences,
            [vertical]: { format, resolution },
          },
        }))
      },
      headerHeight: 0,
      _hasHydrated: false,
      setHasHydrated: (state) => {
        set({
          _hasHydrated: state,
        })
      },
    }),
    {
      name: 'app-store',
      version: 8,
      partialize: (state) => ({
        filtersVisible: state.filtersVisible,
        videoPreviewPreference: state.videoPreviewPreference,
        vfxGridPreviewPreferredVariant: state.vfxGridPreviewPreferredVariant,
        enableVfxPreviewSoundOnHover: state.enableVfxPreviewSoundOnHover,
        audioAssetsLayoutPreference: state.audioAssetsLayoutPreference,
        assetRankingDebug: state.assetRankingDebug,
        downloadFormatPreferences: state.downloadFormatPreferences,
      }),
      migrate: (persistedState, version) => {
        if (!persistedState || typeof persistedState !== 'object') {
          return persistedState
        }

        if (version < 2) {
          return {
            ...(persistedState as PersistedAppState),
            enableVfxPreviewSoundOnHover: false,
            audioAssetsLayoutPreference: 'row',
            downloadFormatPreferences: {},
            downloadExtensionPreferences: {},
          }
        }

        if (version < 3) {
          return {
            ...(persistedState as PersistedAppState),
            audioAssetsLayoutPreference: 'row',
            downloadFormatPreferences: {},
            downloadExtensionPreferences: {},
          }
        }

        if (version < 4) {
          return {
            ...(persistedState as PersistedAppState),
            downloadFormatPreferences: {},
          }
        }

        if (version < 5) {
          const s = persistedState as PersistedAppState & { downloadExtensionPreferences?: unknown }
          const { downloadExtensionPreferences: _dropped, ...rest } = s
          return { ...rest, downloadFormatPreferences: rest.downloadFormatPreferences ?? {} }
        }

        if (version < 6) {
          return {
            ...(persistedState as PersistedAppState),
            assetRankingDebug: {
              enabled: false,
              period: 'year',
            },
          }
        }

        if (version < 7) {
          const state = persistedState as PersistedAppState
          return {
            ...state,
            assetRankingDebug: {
              enabled: state.assetRankingDebug?.enabled ?? false,
              period: state.assetRankingDebug?.period ?? 'year',
              levelOneOnly: false,
            },
          }
        }

        if (version < 8) {
          const state = persistedState as PersistedAppState & { useVfxVideoGridPreview?: unknown }

          return {
            ...state,
            videoPreviewPreference: state.useVfxVideoGridPreview === false ? 'off' : 'auto',
          }
        }

        return persistedState
      },
      onRehydrateStorage: (state) => {
        return () => state.setHasHydrated(true)
      },
    }
  )
)

export { useAppStore }
